import React, { useEffect, useState } from 'react';
import ImageUploader from './components/ImageUploader';
import InlineImageUploader from './components/InlineImageUploader';
import FormActions from './components/FormActions';
import './PortfolioForm.css';
import { normalizeSlug as sharedNormalizeSlug } from '../../hooks/portfolioHelpers';
import useConfirmNavigation from '../../hooks/useConfirmNavigation';

const emptyFeature = () => ({ title: '', description: '' });
const emptyTech = () => ({ name: '', icon: '' });
const emptyCtaButton = () => ({ label: '', url: '', image: '' });

const defaultState = {
  serviceId: '',
  slug: '',
  name: '',
  description: '',
  summary: '',
  company: '',
  image: '',
  websiteUrl: '',
  heroCategory: '',
  heroTitle: '',
  heroSubtitle: '',
  heroTagline: '',
  tags: [],
  sortOrder: 0,
  features: [emptyFeature()],
  techStack: [emptyTech()],
  ctaButtons: [emptyCtaButton()],
  gallery: [''],
  showInHome: false
};

const buildFormState = (data = {}) => {
  const safeData = data && typeof data === 'object' ? data : {};
  const ensureList = (list, fallbackFactory) => {
    if (Array.isArray(list) && list.length) return list.map((item) => ({ ...fallbackFactory(), ...item }));
    return [fallbackFactory()];
  };
  return {
    ...defaultState,
    ...safeData,
    serviceId: safeData.serviceId ? String(safeData.serviceId) : defaultState.serviceId,
    slug: safeData.slug || defaultState.slug,
    sortOrder: Number(safeData.sortOrder ?? defaultState.sortOrder),
    tags: Array.isArray(safeData.tags) ? safeData.tags : [],
    features: ensureList(safeData.features, emptyFeature),
    techStack: ensureList(safeData.techStack, emptyTech),
    ctaButtons: ensureList(safeData.ctaButtons, emptyCtaButton),
    gallery: Array.isArray(safeData.gallery) && safeData.gallery.length ? safeData.gallery : [''],
    showInHome: Boolean(safeData.showInHome)
  };
};

const listToInput = (list) => (Array.isArray(list) ? list.join(', ') : '');

const splitList = (value = '') =>
  value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeSlug = (value = '') => sharedNormalizeSlug(value) || '';
const safeTrim = (value = '') => (typeof value === 'string' ? value.trim() : '');

const sanitizeFeatures = (features) =>
  features
    .map((item) => ({
      title: item.title?.trim() || '',
      description: item.description?.trim() || ''
    }))
    .filter((item) => item.title || item.description);

const sanitizeTech = (techStack) =>
  techStack
    .map((item) => ({
      name: item.name?.trim() || '',
      icon: item.icon?.trim() || ''
    }))
    .filter((item) => item.name);

const sanitizeCtaButtons = (buttons) =>
  buttons
    .map((item) => ({
      label: item.label?.trim() || '',
      url: item.url?.trim() || '',
      image: item.image?.trim() || ''
    }))
    .filter((item) => item.url && item.image);

const sanitizeGallery = (list) =>
  list
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);

const PortfolioForm = ({ initialData, onSubmit, onCancel, isSaving, services = [] }) => {
  const [formData, setFormData] = useState(() => buildFormState(initialData));
  const [tagsInput, setTagsInput] = useState(listToInput(initialData?.tags));
  const [activeTab, setActiveTab] = useState('overview');
  const confirmationMessage = 'You have unsaved portfolio changes. Leave this form?';
  const submitLabel = 'Save Portfolio';

  useConfirmNavigation(!isSaving, confirmationMessage);

  useEffect(() => {
    const nextForm = buildFormState(initialData);
    const nextTags = listToInput(initialData?.tags);
    setFormData(nextForm);
    setTagsInput(nextTags);
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateListItem = (listName, index, field, value) => {
    setFormData((prev) => {
      const nextList = prev[listName].map((item, idx) => (idx === index ? { ...item, [field]: value } : item));
      return { ...prev, [listName]: nextList };
    });
  };

  const updateGalleryItem = (index, value) => {
    setFormData((prev) => {
      const nextGallery = prev.gallery.map((item, idx) => (idx === index ? value : item));
      return { ...prev, gallery: nextGallery };
    });
  };

  const addListItem = (listName, factory) => {
    setFormData((prev) => ({
      ...prev,
      [listName]: [...prev[listName], factory()]
    }));
  };

  const removeListItem = (listName, index, factory) => {
    setFormData((prev) => {
      const filtered = prev[listName].filter((_, idx) => idx !== index);
      return { ...prev, [listName]: filtered.length ? filtered : [factory()] };
    });
  };

  const moveListItem = (listName, index, direction) => {
    setFormData((prev) => {
      const list = prev[listName];
      const targetIndex = index + direction;
      if (!Array.isArray(list) || targetIndex < 0 || targetIndex >= list.length) {
        return prev;
      }
      const nextList = [...list];
      [nextList[index], nextList[targetIndex]] = [nextList[targetIndex], nextList[index]];
      return { ...prev, [listName]: nextList };
    });
  };

  const moveGalleryItem = (index, direction) => {
    setFormData((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.gallery.length) {
        return prev;
      }
      const nextGallery = [...prev.gallery];
      [nextGallery[index], nextGallery[targetIndex]] = [nextGallery[targetIndex], nextGallery[index]];
      return { ...prev, gallery: nextGallery };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = {
      serviceId: Number(formData.serviceId) || '',
      slug: normalizeSlug(formData.slug),
      name: safeTrim(formData.name),
      description: safeTrim(formData.description),
      summary: safeTrim(formData.summary),
      company: safeTrim(formData.company),
      image: safeTrim(formData.image),
      websiteUrl: safeTrim(formData.websiteUrl),
      heroCategory: safeTrim(formData.heroCategory),
      heroTitle: safeTrim(formData.heroTitle),
      heroSubtitle: safeTrim(formData.heroSubtitle),
      heroTagline: safeTrim(formData.heroTagline),
      tags: splitList(tagsInput),
      sortOrder: Number(formData.sortOrder) || 0,
      features: sanitizeFeatures(formData.features),
      techStack: sanitizeTech(formData.techStack),
      ctaButtons: sanitizeCtaButtons(formData.ctaButtons),
      gallery: sanitizeGallery(formData.gallery),
      showInHome: Boolean(formData.showInHome)
    };

    if (!payload.serviceId) {
      alert('Please select a service');
      return;
    }

    if (!payload.slug) {
      alert('Please add a slug (letters, numbers, hyphen only)');
      return;
    }

    if (!payload.name) {
      alert('Project name is required');
      return;
    }

    onSubmit(payload);
  };

  const handleCancelClick = () => {
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    onCancel();
  };

  return (
    <form className="portfolio-form" onSubmit={handleSubmit}>
      <FormActions sticky isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />

      <div className="form-tabs" role="tablist">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'hero', label: 'Hero Content' },
          { key: 'features', label: 'Features' },
          { key: 'cta', label: 'CTA Buttons' },
          { key: 'tech', label: 'Tech Stack' },
          { key: 'gallery', label: 'Gallery' },
          { key: 'download', label: 'Download Section' }
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <section className="portfolio-form-grid">
          <div className="portfolio-form-card">
            <h2>Project Overview</h2>
            <div className="portfolio-form-group">
              <label htmlFor="serviceId">Service *</label>
              <select
                id="serviceId"
                name="serviceId"
                value={formData.serviceId}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select a service</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>{service.name}</option>
                ))}
              </select>
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="name">Project Name *</label>
              <input id="name" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="slug">Slug *</label>
              <input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="case-study-slug"
                required
              />
              <p className="portfolio-form-hint">Used for project details URL.</p>
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="company">Client / Company</label>
              <input id="company" name="company" value={formData.company} onChange={handleChange} />
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="sortOrder">Display Order</label>
              <input
                id="sortOrder"
                name="sortOrder"
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={handleChange}
              />
              <p className="portfolio-form-hint">Lower values surface the project earlier.</p>
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="description">Card Description *</label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </div>
            <div className="portfolio-form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.showInHome}
                  onChange={(event) => setFormData((prev) => ({ ...prev, showInHome: event.target.checked }))}
                />{' '}
                Show on homepage
              </label>
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="websiteUrl">Project URL</label>
              <input id="websiteUrl" name="websiteUrl" value={formData.websiteUrl} onChange={handleChange} placeholder="https://" />
            </div>
            <div className="portfolio-form-group">
              <label htmlFor="tags">Tags (comma separated)</label>
              <input
                id="tags"
                name="tags"
                value={tagsInput}
                onChange={(event) => setTagsInput(event.target.value)}
                placeholder="Fintech, SaaS, React"
              />
            </div>
          </div>

          <div className="portfolio-form-card">
            <h2>Hero Asset</h2>
            <p className="portfolio-form-hint">This image powers the portfolio cards and hero section.</p>
            <ImageUploader value={formData.image} onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))} />
          </div>
        </section>
      )}

      {activeTab === 'hero' && (
        <div className="portfolio-form-card">
          <h2>Hero Content</h2>
          <div className="portfolio-form-group">
            <label htmlFor="heroCategory">Hero Label</label>
            <input id="heroCategory" name="heroCategory" value={formData.heroCategory} onChange={handleChange} placeholder="PROJECT" />
          </div>
          <div className="portfolio-form-group">
            <label htmlFor="heroTitle">Hero Title</label>
            <input id="heroTitle" name="heroTitle" value={formData.heroTitle} onChange={handleChange} />
          </div>
          <div className="portfolio-form-group">
            <label htmlFor="heroSubtitle">Hero Subtitle</label>
            <textarea id="heroSubtitle" name="heroSubtitle" rows="3" value={formData.heroSubtitle} onChange={handleChange} />
          </div>
          <div className="portfolio-form-group">
            <label htmlFor="heroTagline">Hero Tagline</label>
            <input id="heroTagline" name="heroTagline" value={formData.heroTagline} onChange={handleChange} />
          </div>
        </div>
      )}

      {activeTab === 'features' && (
        <div className="portfolio-form-card">
          <div className="portfolio-form-list-header">
            <h2>Project Features</h2>
            <button type="button" className="admin-secondary-btn" onClick={() => addListItem('features', emptyFeature)}>+ Add Feature</button>
          </div>
          {formData.features.map((feature, index) => (
            <div key={`feature-${index}`} className="portfolio-form-list-item">
              <div className="portfolio-form-group">
                <label htmlFor={`feature-title-${index}`}>Title</label>
                <input
                  id={`feature-title-${index}`}
                  value={feature.title}
                  onChange={(event) => updateListItem('features', index, 'title', event.target.value)}
                  placeholder="Feature title"
                />
              </div>
              <div className="portfolio-form-group">
                <label htmlFor={`feature-description-${index}`}>Description</label>
                <textarea
                  id={`feature-description-${index}`}
                  rows="3"
                  value={feature.description}
                  onChange={(event) => updateListItem('features', index, 'description', event.target.value)}
                />
              </div>
              <div className="portfolio-form-list-tools">
                <div className="portfolio-order-controls">
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveListItem('features', index, -1)}
                    disabled={index === 0}
                    aria-label="Move feature up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveListItem('features', index, 1)}
                    disabled={index === formData.features.length - 1}
                    aria-label="Move feature down"
                  >
                    ↓
                  </button>
                  <span className="portfolio-order-index">#{index + 1}</span>
                </div>
                <button
                  type="button"
                  className="portfolio-inline-remove"
                  onClick={() => removeListItem('features', index, emptyFeature)}
                  aria-label="Remove feature"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'cta' && (
        <div className="portfolio-form-card">
          <div className="portfolio-form-list-header">
            <h2>CTA Buttons</h2>
            <button type="button" className="admin-secondary-btn" onClick={() => addListItem('ctaButtons', emptyCtaButton)}>+ Add Button</button>
          </div>
          {formData.ctaButtons.length === 0 && (
            <p className="portfolio-form-hint">Add one or more buttons to highlight live demos, app stores, or other destinations.</p>
          )}
          {formData.ctaButtons.map((button, index) => (
            <div key={`cta-${index}`} className="portfolio-form-list-item">
              <div className="portfolio-form-group">
                <label htmlFor={`cta-label-${index}`}>Label</label>
                <input
                  id={`cta-label-${index}`}
                  value={button.label}
                  onChange={(event) => updateListItem('ctaButtons', index, 'label', event.target.value)}
                  placeholder="Optional label (e.g. App Store)"
                />
              </div>
              <div className="portfolio-form-group">
                <label htmlFor={`cta-url-${index}`}>Button Link *</label>
                <input
                  id={`cta-url-${index}`}
                  value={button.url}
                  onChange={(event) => updateListItem('ctaButtons', index, 'url', event.target.value)}
                  placeholder="https://"
                />
              </div>
              <div className="portfolio-form-group">
                <label>Button Image *</label>
                <InlineImageUploader
                  value={button.image}
                  onChange={(url) => updateListItem('ctaButtons', index, 'image', url)}
                  label={button.image ? 'Replace image' : 'Upload image'}
                  helperText="Upload the button graphic (PNG/SVG)."
                />
              </div>
              <button
                type="button"
                className="portfolio-inline-remove"
                onClick={() => removeListItem('ctaButtons', index, emptyCtaButton)}
                aria-label="Remove CTA button"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'tech' && (
        <div className="portfolio-form-card">
          <div className="portfolio-form-list-header">
            <h2>Tech Stack</h2>
            <button type="button" className="admin-secondary-btn" onClick={() => addListItem('techStack', emptyTech)}>+ Add Tool</button>
          </div>
          {formData.techStack.map((tech, index) => (
            <div key={`tech-${index}`} className="portfolio-form-list-item">
              <div className="portfolio-form-group">
                <label htmlFor={`tech-name-${index}`}>Name</label>
                <input
                  id={`tech-name-${index}`}
                  value={tech.name}
                  onChange={(event) => updateListItem('techStack', index, 'name', event.target.value)}
                  placeholder="React, Flutter, AWS"
                />
              </div>
              <div className="portfolio-form-group">
                <label>Icon</label>
                <InlineImageUploader
                  value={tech.icon}
                  onChange={(url) => updateListItem('techStack', index, 'icon', url)}
                  label={tech.icon ? 'Replace icon' : 'Upload icon'}
                  helperText="Ideal size 100x100 PNG/SVG."
                />
                {tech.icon && (
                  <p className="portfolio-form-hint">Stored URL: {tech.icon}</p>
                )}
              </div>
              <div className="portfolio-form-list-tools">
                <div className="portfolio-order-controls">
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveListItem('techStack', index, -1)}
                    disabled={index === 0}
                    aria-label="Move tech up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveListItem('techStack', index, 1)}
                    disabled={index === formData.techStack.length - 1}
                    aria-label="Move tech down"
                  >
                    ↓
                  </button>
                  <span className="portfolio-order-index">#{index + 1}</span>
                </div>
                <button
                  type="button"
                  className="portfolio-inline-remove"
                  onClick={() => removeListItem('techStack', index, emptyTech)}
                  aria-label="Remove tech"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'gallery' && (
        <div className="portfolio-form-card">
          <div className="portfolio-form-list-header">
            <h2>Gallery</h2>
            <button type="button" className="admin-secondary-btn" onClick={() => addListItem('gallery', () => '')}>+ Add Image</button>
          </div>
          {formData.gallery.map((url, index) => (
            <div key={`gallery-${index}`} className="portfolio-form-list-item gallery-item">
              <div className="portfolio-form-group">
                <label>Image</label>
                <InlineImageUploader
                  value={url}
                  onChange={(uploadedUrl) => updateGalleryItem(index, uploadedUrl)}
                  label={url ? 'Replace image' : 'Upload image'}
                  helperText="Shown on the project gallery carousel."
                />
                {url && (
                  <p className="portfolio-form-hint">Stored URL: {url}</p>
                )}
              </div>
              <div className="portfolio-form-list-tools">
                <div className="portfolio-order-controls">
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveGalleryItem(index, -1)}
                    disabled={index === 0}
                    aria-label="Move image up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="portfolio-order-btn"
                    onClick={() => moveGalleryItem(index, 1)}
                    disabled={index === formData.gallery.length - 1}
                    aria-label="Move image down"
                  >
                    ↓
                  </button>
                  <span className="portfolio-order-index">#{index + 1}</span>
                </div>
                <button
                  type="button"
                  className="portfolio-inline-remove"
                  onClick={() => removeListItem('gallery', index, () => '')}
                  aria-label="Remove gallery item"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'download' && (
        <div className="portfolio-form-card">
          <h2>Project Download Section</h2>
          <div className="portfolio-form-group">
            <label htmlFor="heroTagline">Download Title</label>
            <input
              id="heroTagline"
              name="heroTagline"
              value={formData.heroTagline}
              onChange={handleChange}
              placeholder="Call-to-action title"
            />
          </div>
          <div className="portfolio-form-group">
            <label htmlFor="summary">Download Description *</label>
            <textarea
              id="summary"
              name="summary"
              rows="4"
              value={formData.summary}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      )}

      <FormActions variant="footer" isSaving={isSaving} onCancel={handleCancelClick} submitLabel={submitLabel} />
    </form>
  );
};

export default PortfolioForm;
